import { paramParsers, StringParamParser } from './param-parsers';

interface QueryParamWrapper<TV> {
    set(url: URLSearchParams, key: string, value: TV): void;
    get(url: URLSearchParams, key: string): TV | undefined;
}

// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
type HandlersFromValues<Values extends Record<string, any | undefined>> = {
    [Prop in keyof Values]: QueryParamWrapper<Values[Prop]>;
};

const querySingleParam = <TP>(parser: StringParamParser<TP>): QueryParamWrapper<TP> => ({
    set(url, key, val: TP) {
        if (val === undefined || val === null) {
            return;
        }

        const query = parser.stringify(val as any);

        if (query !== undefined) {
            url.append(key as string, query);
        }
    },
    get(url, key) {
        const param = url.get(key as string);

        if (!param) {
            return;
        }

        return parser.parse(param);
    },
});

const queryArrayParam = <TP>(parser: StringParamParser<TP>): QueryParamWrapper<TP[]> => ({
    set(url, key, val: TP[]) {
        if (val === undefined || val === null || !Array.isArray(val) || !val.length) {
            return;
        }

        val.forEach(item => {
            const query = parser.stringify(item);

            if (query !== undefined) {
                url.append(key as string, query);
            }
        });
    },
    get(url, key) {
        const params = url.getAll(key as string);

        if (!params || !Array.isArray(params) || !params.length) {
            return;
        }

        const out: TP[] = [];

        params.forEach(item => {
            const val = parser.parse(item);

            if (val !== undefined) {
                out.push(val);
            }
        });

        return out;
    },
});

// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
export class QueryParamsHandler<Values extends Record<string, any | undefined>> {
    static readonly parserString = querySingleParam(paramParsers.uri);
    static readonly parserNumber = querySingleParam(paramParsers.number);
    static readonly parserDate = querySingleParam(paramParsers.date);
    static readonly parserBoolean = querySingleParam(paramParsers.bool);
    static readonly parserNumberArray = queryArrayParam(paramParsers.number);
    static readonly parserStringArray = queryArrayParam(paramParsers.uri);

    static readonly querySingleParam = querySingleParam;
    static readonly queryArrayParam = queryArrayParam;

    constructor(private handlers: HandlersFromValues<Values>) {}

    static from<T extends Record<string, any>>(handlers: HandlersFromValues<T>) {
        return new QueryParamsHandler<T>(handlers);
    }

    toString(params: Values): string {
        const keys = Object.keys(this.handlers);
        const url = new URLSearchParams();

        keys.forEach(key => {
            const handler = this.handlers[key];
            const val: any = params[key];

            if (handler && val !== undefined && val !== null) {
                handler.set(url, key, val);
            }
        });

        return url.toString();
    }

    parse(query?: string): Values {
        const url = new URLSearchParams(query ?? '');
        const keys = Object.keys(this.handlers);

        return keys.reduce((acc, key) => {
            const handler = this.handlers[key];

            if (!handler) {
                return acc;
            }

            const val = handler.get(url, key);

            if (val === undefined) {
                return acc;
            }

            return { ...acc, [key]: val };
        }, {}) as Values;
    }
}
